const SQL_KEYWORDS = [
    "select", "from", "where", "join", "left join", "right join", "inner join", "outer join",
    "group by", "order by", "having", "limit", "offset", "insert into", "values", "update", "set",
    "delete from", "create table", "alter table", "drop table", "union", "union all", "and", "or",
    "as", "on", "case", "when", "then", "else", "end", "distinct", "returning",
];

export const formatSql = (sql = "") => {
    let output = String(sql).trim().replace(/\s+/g, " ");
    const lineKeywords = [
        "select", "from", "where", "left join", "right join", "inner join", "outer join", "join",
        "group by", "order by", "having", "limit", "offset", "insert into", "values", "update", "set",
        "delete from", "returning", "union all", "union",
    ];

    lineKeywords.forEach((keyword) => {
        const re = new RegExp(`\\s+${keyword.replace(" ", "\\s+")}\\s+`, "gi");
        output = output.replace(re, `\n${keyword.toUpperCase()} `);
    });

    output = output.replace(/^select\s+/i, "SELECT ");
    output = output.replace(/\s*,\s*/g, ",\n    ");
    return output.trim();
};

export const transformSqlKeywords = (sql = "", mode = "upper") => {
    let output = String(sql);
    [...SQL_KEYWORDS].sort((a, b) => b.length - a.length).forEach((keyword) => {
        const re = new RegExp(`\\b${keyword.replace(" ", "\\s+")}\\b`, "gi");
        output = output.replace(re, mode === "lower" ? keyword.toLowerCase() : keyword.toUpperCase());
    });
    return output;
};

const scalarToYaml = (value) => {
    if (value === null) return "null";
    if (typeof value === "boolean" || typeof value === "number") return String(value);
    const text = String(value);
    if (!text || /[:#\-\[\]{},&*!?|>'"%@`\n\r]/.test(text) || /^\s|\s$/.test(text)) return JSON.stringify(text);
    return text;
};

export const jsonToYaml = (value, indent = 0) => {
    const pad = "  ".repeat(indent);
    if (Array.isArray(value)) {
        return value.map((item) => {
            if (item && typeof item === "object") return `${pad}-\n${jsonToYaml(item, indent + 1)}`;
            return `${pad}- ${scalarToYaml(item)}`;
        }).join("\n");
    }

    if (value && typeof value === "object") {
        return Object.entries(value).map(([key, item]) => {
            if (item && typeof item === "object") return `${pad}${key}:\n${jsonToYaml(item, indent + 1)}`;
            return `${pad}${key}: ${scalarToYaml(item)}`;
        }).join("\n");
    }

    return `${pad}${scalarToYaml(value)}`;
};

const parseYamlScalar = (raw) => {
    const text = String(raw).trim();
    if (!text) return "";
    if (text === "null" || text === "~") return null;
    if (text === "true") return true;
    if (text === "false") return false;
    if (/^-?\d+(\.\d+)?$/.test(text)) return Number(text);
    if ((text.startsWith('"') && text.endsWith('"')) || (text.startsWith("'") && text.endsWith("'"))) {
        try { return JSON.parse(text.startsWith("'") ? `"${text.slice(1, -1).replace(/"/g, '\\"')}"` : text); }
        catch (_) { return text.slice(1, -1); }
    }
    return text;
};

// Parser intentionally focused on the common YAML subset used for configs:
// mappings, arrays, indentation and scalar values. It does not execute tags or anchors.
export const yamlToJsonValue = (yaml = "") => {
    const lines = String(yaml).replace(/\t/g, "  ").split(/\r?\n/)
        .map((raw) => ({
            raw,
            indent: raw.match(/^\s*/)?.[0].length ?? 0,
            text: raw.trim(),
        }))
        .filter((line) => line.text && !line.text.startsWith("#"));

    if (!lines.length) return {};

    const root = lines[0].text.startsWith("-") ? [] : {};
    const stack = [{ indent: -1, value: root }];

    const nextMeaningful = (index) => lines[index + 1] ?? null;

    lines.forEach((line, index) => {
        while (stack.length > 1 && line.indent <= stack[stack.length - 1].indent) stack.pop();
        const parent = stack[stack.length - 1].value;

        if (line.text.startsWith("-")) {
            if (!Array.isArray(parent)) throw new Error(`Lista inesperada en línea ${index + 1}`);
            const rest = line.text.slice(1).trim();
            if (!rest) {
                const next = nextMeaningful(index);
                const child = next?.text.startsWith("-") ? [] : {};
                parent.push(child);
                stack.push({ indent: line.indent, value: child });
                return;
            }
            const separator = rest.indexOf(":");
            if (separator > 0) {
                const child = {};
                const key = rest.slice(0, separator).trim();
                const rawValue = rest.slice(separator + 1).trim();
                child[key] = rawValue ? parseYamlScalar(rawValue) : {};
                parent.push(child);
                stack.push({ indent: line.indent, value: child });
                return;
            }
            parent.push(parseYamlScalar(rest));
            return;
        }

        const separator = line.text.indexOf(":");
        if (separator < 1 || Array.isArray(parent)) throw new Error(`Formato YAML no soportado en línea ${index + 1}`);
        const key = line.text.slice(0, separator).trim();
        const rawValue = line.text.slice(separator + 1).trim();

        if (rawValue) {
            parent[key] = parseYamlScalar(rawValue);
            return;
        }

        const next = nextMeaningful(index);
        const child = next && next.indent > line.indent && next.text.startsWith("-") ? [] : {};
        parent[key] = child;
        stack.push({ indent: line.indent, value: child });
    });

    return root;
};

export const decodeBase64UrlJson = (value = "") => {
    const normalized = String(value).replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    const text = new TextDecoder().decode(bytes);
    return JSON.parse(text);
};

export const textToBase64 = (text = "") => {
    const bytes = new TextEncoder().encode(String(text));
    let binary = "";
    bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
    return btoa(binary);
};

export const base64ToText = (value = "") => {
    const binary = atob(String(value).replace(/\s/g, ""));
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
};

export const textToHex = (text = "") => Array.from(new TextEncoder().encode(String(text)))
    .map((byte) => byte.toString(16).padStart(2, "0")).join("");

export const hexToText = (hex = "") => {
    const clean = String(hex).replace(/\s+/g, "");
    if (!/^[0-9a-f]*$/i.test(clean) || clean.length % 2) throw new Error("Hexadecimal inválido");
    const bytes = new Uint8Array(clean.match(/.{2}/g)?.map((part) => parseInt(part, 16)) ?? []);
    return new TextDecoder().decode(bytes);
};

export const toUnicodeEscapes = (text = "") => Array.from(String(text)).map((char) => {
    const code = char.codePointAt(0);
    return code > 0xffff
        ? `\\u{${code.toString(16).toUpperCase()}}`
        : `\\u${code.toString(16).padStart(4, "0").toUpperCase()}`;
}).join("");

export const fromUnicodeEscapes = (text = "") => String(text)
    .replace(/\\u\{([0-9a-f]+)\}/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/\\u([0-9a-f]{4})/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

export const htmlEncode = (text = "") => String(text).replace(/[&<>"']/g, (char) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#039;",
}[char]));

export const htmlDecode = (text = "") => {
    const area = document.createElement("textarea");
    area.innerHTML = String(text);
    return area.value;
};
